import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { plural } from 'lib/plural';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isControlFreemium, isHardFreemium, isSoftFreemium } from 'reactApp/appHelpers/featuresHelpers/features/freemium';
import { abHeavyFileBuy, abHeavyFileBuyV3 } from 'reactApp/appHelpers/featuresHelpers/features/heavyFileBuy';
import { uploadBigFileModalAb } from 'reactApp/appHelpers/featuresHelpers/features/uploadBigFileModal';
import { uploadBigFileNewText } from 'reactApp/appHelpers/featuresHelpers/features/uploadBigFileNewText';
import { useButtons } from 'reactApp/components/UploadBigFileDialog/hooks/useButtons';
import {
    type Content,
    type IBigFileModalContent,
    EShowTariffType,
    FILES_PLURAL,
} from 'reactApp/components/UploadBigFileDialog/UploadBigFileDialog.types';
import { SPACE_LIST } from 'reactApp/constants/spaceList';
import { getOwner } from 'reactApp/modules/folderAccessControlList/folderAccessControlList.selectors';
import { getPublicOwner, getPublicUploadLimit } from 'reactApp/modules/public/public.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { useShouldShowFreemiumHeavyFile } from 'reactApp/ui/Freemium/useShouldShowFreemiumHeavyFile';
import { NameComponent } from 'reactApp/ui/Name/Name';
import { formatPrice, getPriceStringWithPeriod } from 'reactApp/utils/priceHelpers';
import { truncateTextMiddle } from 'reactApp/utils/textHelpers';

const getHeader = ({ name, files, uploadLimit, stage, shouldShowFreemium }) => {
    switch (true) {
        case Boolean(shouldShowFreemium && name):
            return <>Пока не получится загрузить файл «{truncateTextMiddle(name || '', 22)}»</>;

        /* tempexp_18955-start */
        case Boolean(abHeavyFileBuyV3 && stage === 'usual'):
            return <>Пока не получится загрузить файл «{truncateTextMiddle(name || '', 22)}»</>;
        case Boolean(abHeavyFileBuyV3 && stage === 'transact'):
            return <>Есть другое решение — оплатите загрузку только одного файла</>;
        /* tempexp_18955-end */

        /* tempexp_18869-start */
        case Boolean(abHeavyFileBuy):
            return (
                <>
                    Файл «{truncateTextMiddle(name || '', 22)}» весит больше {bytesToNDigits(uploadLimit, 3).value}
                </>
            );
        /* tempexp_18869-end */

        case Boolean(files):
            return 'Невозможно загрузить файлы';

        /* tempexp_18603-start */
        case uploadBigFileNewText:
            return <>Файлы больше {bytesToNDigits(uploadLimit, 3).value} можно загружать только с&nbsp;подпиской</>;

        /* tempexp_18603-end */
        default:
            return <>Невозможно загрузить файл «{truncateTextMiddle(name || '', 22)}»</>;
    }
};

export const useContent = ({ tariffInfo, numberOfFiles, file, files, callbacks, stage }: IBigFileModalContent): Content => {
    const { uploadLimit } = useSelector(UserSelectors.getUserData);
    const publicUploadLimit = useSelector(getPublicUploadLimit);
    const shouldShowFreemium = useShouldShowFreemiumHeavyFile();

    const currentStorage = useSelector(getCurrentStorage);
    const { isPublic } = getStorage(currentStorage);

    const publicOwner = useSelector(getPublicOwner);
    const acOwner = useSelector(getOwner);
    const owner = isPublic ? publicOwner : acOwner;

    const currentUserEmail = useSelector(UserSelectors.getEmail);

    const isStrangerOwner = !!currentUserEmail && !!owner?.email && currentUserEmail !== owner?.email;

    const { usualProduct, showTariffType, transactProduct } = tariffInfo;

    const annotation = useMemo(() => {
        switch (true) {
            case Boolean(shouldShowFreemium && isSoftFreemium):
                return (
                    <>
                        Файл весит больше 1{'\u00A0'}ГБ. Подключите пробную подписку бесплатно — с ней можно отправлять файлы до{'\u00A0'}
                        100{'\u00A0'}ГБ
                    </>
                );

            case Boolean(shouldShowFreemium && isHardFreemium):
                return (
                    <>
                        Файл весит больше 1{'\u00A0'}ГБ. Подключите пробную подписку на 7 дней бесплатно — с ней можно отправлять файлы до
                        {'\u00A0'}100{'\u00A0'}ГБ
                    </>
                );

            case Boolean(shouldShowFreemium && isControlFreemium):
                return (
                    <>
                        Файл весит больше 1{'\u00A0'}ГБ. Подключите пробную подписку на 30 дней — с ней можно отправлять файлы до{'\u00A0'}
                        100{'\u00A0'}ГБ
                    </>
                );

            case Boolean(isStrangerOwner && isPublic && publicUploadLimit):
                return (
                    <>
                        Файл слишком тяжёлый. Чтобы вы могли загрузить файлы больше {bytesToNDigits(publicUploadLimit || 0, 3).value},
                        владелец папки должен подключить один из платных тарифов.
                    </>
                );

            /* tempexp_18955-start */
            case Boolean(abHeavyFileBuyV3 && stage === 'usual'):
                return (
                    <>
                        Файл весит больше {bytesToNDigits(uploadLimit, 3).value}. Но{'\u00A0'}с{'\u00A0'}подпиской Mail{'\u00A0'}Space можно
                        загружать файлы потяжелее{'\u00A0'}— до{'\u00A0'}100{'\u00A0'}ГБ
                    </>
                );
            case Boolean(abHeavyFileBuyV3 && stage === 'transact'):
                return (
                    <>
                        Подключить подписку Mail{'\u00A0'}Space можно и{'\u00A0'}позже, а{'\u00A0'}сейчас загрузите файл за{'\u00A0'}
                        {formatPrice(transactProduct?.price || 0)}
                        {'\u00A0'}₽
                    </>
                );
            /* tempexp_18955-end */

            /* tempexp_18869-start */
            case Boolean(abHeavyFileBuy):
                return 'Оплатите разовую загрузку или оформите подписку Mail Space, чтобы загружать много файлов. Поместятся файлы до 100 ГБ каждый';
            /* tempexp_18869-end */

            /* tempexp_17604-start */
            case uploadBigFileModalAb === 'b':
                return (
                    <>
                        Файл слишком тяжёлый. Чтобы загружать файлы больше {bytesToNDigits(uploadLimit, 3).value}, подключите подписку Mail
                        Space
                        {showTariffType === EShowTariffType.USUAL_TARIFF &&
                            usualProduct &&
                            ` — ${usualProduct?.space?.value} за ${getPriceStringWithPeriod(usualProduct)}`}
                    </>
                );
            /* tempexp_17604-end */

            /* tempexp_18603-start */
            case uploadBigFileNewText:
                return (
                    <>
                        Попробуйте Mail Space, чтобы загружать файлы размером до&nbsp;100&nbsp;ГБ, а&nbsp;ещё получить{' '}
                        {bytesToNDigits(callbacks.proposedTariff?.space.original || SPACE_LIST.gb_128, 3).value} в&nbsp;Облаке
                    </>
                );
            /* tempexp_18603-end */

            default:
                return (
                    <>
                        Файл слишком тяжёлый. Чтобы загружать файлы больше {bytesToNDigits(uploadLimit, 3).value}, подключите один из
                        платных тарифов.
                    </>
                );
        }
    }, [isPublic, isStrangerOwner, publicUploadLimit, uploadLimit, stage, shouldShowFreemium]);

    const description = useMemo(() => {
        if (files) {
            return <>{FILES_PLURAL[plural(files.number)].replace('{count}', `${files.number}`)}</>;
        }

        if (uploadBigFileNewText) {
            return <NameComponent name={file?.name || ''} truncate />;
        }

        return (
            <>
                <span>
                    <NameComponent name={file?.name || ''} truncate />{' '}
                </span>
                не может быть загружен
            </>
        );
    }, [file?.name, files]);

    const name = file?.name;
    const header = useMemo(
        () => getHeader({ name, files, uploadLimit, stage, shouldShowFreemium }),
        [name, files, uploadLimit, stage, shouldShowFreemium]
    );

    const buttonActions = useButtons({ tariffInfo, numberOfFiles, files, callbacks, stage });

    return {
        header,
        annotation,
        actions: buttonActions,
        description,
    };
};
